import type {
  MapleMythosReasoningMode,
  MapleMythosReturnMode,
} from '@/lib/demythos/types';

export type DeMythosCommand =
  | { type: 'help' }
  | { type: 'run'; prompt?: string }
  | { type: 'model'; modelId: string }
  | { type: 'loops'; value: number }
  | { type: 'mode'; value: MapleMythosReasoningMode }
  | { type: 'return'; value: MapleMythosReturnMode }
  | { type: 'budget'; budget: { tokenBudget?: number; creditBudget?: number; timeBudgetMs?: number } }
  | { type: 'ssh'; action: 'status' | 'list' | 'off' | 'grant'; reference?: string }
  | { type: 'kb'; action: 'status' | 'save' | 'auto'; enabled?: boolean; name?: string }
  | { type: 'status' }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'stop' }
  | { type: 'delete' }
  | { type: 'transcript' }
  | { type: 'result' }
  | { type: 'clear' }
  | { type: 'ultra' }
  | { type: 'unknown'; message: string };

export const DEMYTHOS_HELP_LINES = [
  'DeMythos commands:',
  '/run <prompt>        Start a looped reasoning run with the supplied prompt.',
  '/model <id>          Switch the selected model.',
  '/loops <n>           Set the maximum loop count.',
  '/mode <balanced|skeptical|expansive>',
  '/return <final-answer|summary-answer|full-state>',
  '/budget <tokens N|credits N|time 60s>   Update one or more budgets.',
  '/ssh                 Show the current DeMythos SSH grant.',
  '/ssh list            List saved MapleOS Terminal SSH profiles.',
  '/ssh <profile>       Grant a saved SSH profile to DeMythos for loop execution.',
  '/ssh off             Remove the current DeMythos SSH grant.',
  '/kb                  Show knowledge base capture status.',
  '/kb auto on|off      Enable or disable automatic KB creation when runs finish.',
  '/kb save [name]      Save the active run findings into a new knowledge base.',
  '/ultra               Clear token/credit/time budgets and raise loops to the highest app setting.',
  '/status              Show the active run status.',
  '/pause               Pause the active run.',
  '/resume              Resume the active run.',
  '/stop                Stop the active run.',
  '/delete              Delete the active completed/failed/stopped run.',
  '/transcript          Export the active transcript.',
  '/result              Print the latest structured result.',
  '/clear               Clear the current DeMythos terminal transcript.',
  '/help                Show this command list.',
].join('\n');

function parseTimeBudget(value: string) {
  const trimmed = value.trim().toLowerCase();
  const match = trimmed.match(/^(\d+(?:\.\d+)?)(ms|s|m)?$/);
  if (!match) return null;

  const amount = Number(match[1]);
  const unit = match[2] || 's';

  if (!Number.isFinite(amount) || amount < 0) return null;
  if (unit === 'ms') return Math.round(amount);
  if (unit === 'm') return Math.round(amount * 60_000);
  return Math.round(amount * 1000);
}

export function parseDeMythosCommand(input: string): DeMythosCommand | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('/')) {
    return { type: 'run', prompt: trimmed };
  }

  const [command, ...rest] = trimmed.slice(1).split(/\s+/);
  const argument = rest.join(' ').trim();

  switch ((command || '').toLowerCase()) {
    case 'help':
      return { type: 'help' };
    case 'run':
      return { type: 'run', prompt: argument || undefined };
    case 'model':
      return argument ? { type: 'model', modelId: argument } : { type: 'unknown', message: 'Usage: /model <id>' };
    case 'loops': {
      const value = Number(argument);
      return Number.isFinite(value) && value > 0
        ? { type: 'loops', value }
        : { type: 'unknown', message: 'Usage: /loops <positive number>' };
    }
    case 'mode':
      return ['balanced', 'skeptical', 'expansive'].includes(argument)
        ? { type: 'mode', value: argument as MapleMythosReasoningMode }
        : { type: 'unknown', message: 'Usage: /mode <balanced|skeptical|expansive>' };
    case 'return':
      return ['final-answer', 'summary-answer', 'full-state'].includes(argument)
        ? { type: 'return', value: argument as MapleMythosReturnMode }
        : { type: 'unknown', message: 'Usage: /return <final-answer|summary-answer|full-state>' };
    case 'budget': {
      if (!argument) {
        return { type: 'unknown', message: 'Usage: /budget <tokens N|credits N|time 60s>' };
      }

      const segments = argument.split(/[;,]/).map((segment) => segment.trim()).filter(Boolean);
      const budget: { tokenBudget?: number; creditBudget?: number; timeBudgetMs?: number } = {};

      for (const segment of segments) {
        const [kind, rawValue] = segment.split(/\s+/, 2);
        if (!kind || !rawValue) {
          const implicitTokens = Number(segment);
          if (Number.isFinite(implicitTokens) && implicitTokens > 0) {
            budget.tokenBudget = implicitTokens;
            continue;
          }
          return { type: 'unknown', message: 'Usage: /budget <tokens N|credits N|time 60s>' };
        }

        if (kind === 'tokens') {
          const tokenBudget = Number(rawValue);
          if (!Number.isFinite(tokenBudget) || tokenBudget <= 0) {
            return { type: 'unknown', message: 'Token budget must be a positive number.' };
          }
          budget.tokenBudget = tokenBudget;
          continue;
        }

        if (kind === 'credits') {
          const creditBudget = Number(rawValue);
          if (!Number.isFinite(creditBudget) || creditBudget < 0) {
            return { type: 'unknown', message: 'Credit budget must be zero or higher.' };
          }
          budget.creditBudget = creditBudget;
          continue;
        }

        if (kind === 'time') {
          const timeBudgetMs = parseTimeBudget(rawValue);
          if (timeBudgetMs === null) {
            return { type: 'unknown', message: 'Time budget must look like 30s, 2m, or 1500ms.' };
          }
          budget.timeBudgetMs = timeBudgetMs;
          continue;
        }

        return { type: 'unknown', message: 'Usage: /budget <tokens N|credits N|time 60s>' };
      }

      return { type: 'budget', budget };
    }
    case 'ssh': {
      if (!argument) {
        return { type: 'ssh', action: 'status' };
      }
      if (argument === 'list') {
        return { type: 'ssh', action: 'list' };
      }
      if (['off', 'none', 'disable'].includes(argument)) {
        return { type: 'ssh', action: 'off' };
      }
      return { type: 'ssh', action: 'grant', reference: argument };
    }
    case 'kb': {
      if (!argument) {
        return { type: 'kb', action: 'status' };
      }

      const lowerArgument = argument.toLowerCase();
      if (lowerArgument === 'save') {
        return { type: 'kb', action: 'save' };
      }
      if (lowerArgument.startsWith('save ')) {
        return { type: 'kb', action: 'save', name: argument.slice(5).trim() || undefined };
      }
      if (lowerArgument === 'auto on') {
        return { type: 'kb', action: 'auto', enabled: true };
      }
      if (lowerArgument === 'auto off') {
        return { type: 'kb', action: 'auto', enabled: false };
      }

      return { type: 'unknown', message: 'Usage: /kb | /kb auto on|off | /kb save [name]' };
    }
    case 'ultra':
      return { type: 'ultra' };
    case 'status':
      return { type: 'status' };
    case 'pause':
      return { type: 'pause' };
    case 'resume':
      return { type: 'resume' };
    case 'stop':
      return { type: 'stop' };
    case 'delete':
    case 'reset':
      return { type: 'delete' };
    case 'transcript':
      return { type: 'transcript' };
    case 'result':
      return { type: 'result' };
    case 'clear':
      return { type: 'clear' };
    default:
      return { type: 'unknown', message: `Unknown DeMythos command: /${command}` };
  }
}

export function formatTimeBudget(timeBudgetMs?: number) {
  if (!timeBudgetMs) return '—';
  if (timeBudgetMs % 60_000 === 0) return `${timeBudgetMs / 60_000}m`;
  if (timeBudgetMs % 1000 === 0) return `${timeBudgetMs / 1000}s`;
  return `${timeBudgetMs}ms`;
}